import type { HTMLAttributes, ReactNode } from "react";
import Search from "@mui/icons-material/Search";
import type { AutocompleteRenderOptionState } from "@mui/material";
import { Autocomplete, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Element } from "react-scroll";

import type { TranslationKeyAndOptions } from "@thalamos/vision-common";
import { tk, translate } from "@thalamos/vision-common";

import type { TextFieldProps } from "../FormBuilder/TextField/TextField.js";
import { TextField } from "../FormBuilder/TextField/TextField.js";

// A JSX element is an immutable description, not a component instance: React
// mounts a separate icon wherever this is rendered. One shared element keeps
// `startAdornment`'s identity stable; MUI's InputBase compares it in an effect
// that sets FormControl state, so a fresh element per render fires on every
// keystroke.
const SEARCH_ADORNMENT = <Search fontSize="medium" color="primary" />;

type IconType = "info" | "error" | "warning";

export interface TypeaheadProps<T> extends TextFieldProps {
  label?: TranslationKeyAndOptions;
  options: T[];
  getOptionLabel?: (option: string | T) => string;
  onInputChange?: (value: string) => void;
  // Fired when a value is committed: an option object when picked from the
  // dropdown, or a string when free text is allowed. Lets the consumer capture
  // the selection directly rather than relying on the input text round-tripping
  // back through onInputChange.
  onChange?: (value: string | T | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: TranslationKeyAndOptions;
  errorMessage?: string;
  subtext?: string;
  subtextIcon?: IconType;
  inputValue?: string;
  testId?: string;
  maxLength?: number;
  allowFreeText?: boolean;
  name: string;
  optionsKey?: string;
  groupBy?: (option: T) => string;
  getOptionSubLabel?: (option: T) => string;
  filterOptions?: (
    options: T[],
    state: { inputValue: string; getOptionLabel: (option: T) => string },
  ) => T[];
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState,
  ) => ReactNode;
}

export const Typeahead = <T,>(props: TypeaheadProps<T>) => {
  const {
    label,
    options,
    getOptionLabel,
    onInputChange,
    onChange,
    disabled = false,
    loading = false,
    inputValue = "",
    maxLength,
    allowFreeText = true,
    name,
    optionsKey = "label",
    onBlur,
    groupBy,
    getOptionSubLabel,
    filterOptions,
    renderOption,
    placeholder,
    ...rest
  } = props;

  const theme = useTheme();

  const testId = props.testId ? `field:${props.testId}` : undefined;

  // A stable identity for each option, used wherever options are keyed or
  // compared — without it MUI reuses/duplicates rows as the filtered list
  // reorders and loses track of the highlighted/selected option.
  const keyOf = (option: string | T): string => {
    if (typeof option === "string") {
      return option;
    }
    const raw = (option as Record<string, unknown>)[optionsKey];
    if (typeof raw === "string" || typeof raw === "number") {
      return String(raw);
    }
    return getOptionLabel ? getOptionLabel(option) : "";
  };

  const { t } = useTranslation();

  return (
    <Element name={name} data-testid={testId} style={{ position: "relative" }}>
      <Autocomplete
        filterOptions={
          // Once the input exactly matches an option (a committed selection),
          // stop filtering so reopening the dropdown shows the full list
          // rather than just the committed option.
          filterOptions ??
          (options.some(
            (option) =>
              keyOf(option).toLowerCase() === inputValue.toLowerCase(),
          )
            ? (options) => options
            : undefined)
        }
        fullWidth
        onBlur={onBlur}
        freeSolo={allowFreeText}
        // `value` is derived from the controlled `inputValue`: the consumer commits
        // a selection by updating the text, and a picked option is reported
        // through `onChange`. Never mirror it into state with an effect: an update
        // scheduled from an effect on every keystroke leaves work pending after
        // each commit, and once keystrokes outrun React's scheduler that counts
        // towards the nested-update limit ("Maximum update depth exceeded").
        value={inputValue}
        inputValue={inputValue}
        disabled={disabled}
        loading={loading}
        options={options}
        includeInputInList
        getOptionLabel={getOptionLabel}
        getOptionKey={keyOf}
        isOptionEqualToValue={(option, value) => keyOf(option) === keyOf(value)}
        groupBy={groupBy}
        onChange={(_e, v) => {
          onChange?.(v);
        }}
        onInputChange={(_e, v) => {
          onInputChange?.(v);
        }}
        renderOption={renderOption}
        renderGroup={(params) => (
          <Box key={params.key}>
            <Box
              sx={{
                m: 2,
                borderBottom: `1px solid ${theme.palette.primary.main}`,
                paddingBottom: theme.spacing(1),
              }}
            >
              {params.group}
            </Box>
            {params.children}
          </Box>
        )}
        disableClearable
        renderInput={(params) => {
          const ariaLabel = translate(
            t,
            label ?? placeholder ?? { translationKey: tk("common.search") },
          );

          return (
            <TextField
              {...rest}
              {...params}
              label={label}
              InputProps={{
                ...params.InputProps,
                autoComplete: "off",
                type: "search",
                inputProps: {
                  maxLength,
                  "data-testid": "text-field",
                  "aria-label": ariaLabel,
                  ...params.inputProps,
                },
                startAdornment: SEARCH_ADORNMENT,
              }}
            />
          );
        }}
        slotProps={{
          listbox: {
            "data-testid": testId,
          } as HTMLAttributes<HTMLUListElement> & { "data-testid"?: string },
        }}
      />
    </Element>
  );
};
